import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { useLocalizedPath } from "../components/Header";
import ReviewSection from "../components/ReviewSection";
import {
  Phone,
  Mail,
  Home as HomeIcon,
  Hammer,
  Wrench,
  ArrowRight,
} from "lucide-react";

// Original uploaded logo (used for all languages until dedicated translations are provided)
const logoUrl = "https://customer-assets-lqy194kg.emergentagent.net/job_0f39ab8a-1072-44b6-b313-3f5ac652d65b/artifacts/rqvub9rs_1000107640.png";

export default function Home() {
  const { t } = useTranslation();
  const { getPath } = useLocalizedPath();

  const services = [
    {
      title: t('home.servicesPreview.drywall.title'),
      description: t('home.servicesPreview.drywall.description'),
      icon: HomeIcon,
      link: getPath('drywall'),
    },
    {
      title: t('home.servicesPreview.flooring.title'),
      description: t('home.servicesPreview.flooring.description'),
      icon: Hammer,
      link: getPath('flooring'),
    },
    {
      title: t('home.servicesPreview.sanierung.title'),
      description: t('home.servicesPreview.sanierung.description'),
      icon: Wrench,
      link: null,
    },
  ];

  return (
    <div data-testid="home-page">
      {/* Hero Section - Logo as integrated visual element */}
      <section className="bg-[#1e2a3a]" data-testid="hero-section">
        {/* Logo - Seamlessly integrated */}
        <div className="w-full">
          <img
            src={logoUrl}
            alt={t('home.hero.logoAlt')}
            className="w-full h-auto block"
            data-testid="hero-logo"
          />
        </div>

        {/* CTA Buttons */}
        <div className="w-full px-4 sm:px-6 pb-8 sm:pb-10 md:pb-12 pt-4 sm:pt-6">
          <div className="max-w-md mx-auto flex flex-col gap-3 sm:gap-4">
            <Button
              asChild
              size="lg"
              className="w-full bg-orange-500 hover:bg-orange-600 text-white text-base sm:text-lg h-12 sm:h-14 px-6 shadow-lg hover:shadow-xl transition-all cta-pulse"
              data-testid="hero-cta-primary"
            >
              <a href="mailto:info@mtrenovierung.de?subject=Anfrage%20für%20Renovierungsarbeiten&body=Guten%20Tag%2C%0A%0AIch%20interessiere%20mich%20für%20Ihre%20Dienstleistungen%20und%20bitte%20um%20ein%20unverbindliches%20Angebot.%0A%0AMit%20freundlichen%20Grüßen">
                <Mail className="mr-2 h-5 w-5" />
                {t('home.hero.ctaPrimary')}
              </a>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="w-full border-2 border-white text-white hover:bg-white hover:text-[#1e2a3a] text-base sm:text-lg h-12 sm:h-14 px-6 bg-transparent"
              data-testid="hero-cta-secondary"
            >
              <a href="tel:+4915207684235">
                <Phone className="mr-2 h-5 w-5" />
                {t('home.hero.ctaSecondary')}
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Benefits Section - Why MT Renovierung */}
      <section className="py-16 md:py-24 bg-white" data-testid="benefits-section">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <div className="text-center mb-10 md:mb-12">
            <h2 className="text-3xl md:text-4xl font-bold font-['Manrope'] text-[#2D3748]">
              {t('home.benefits.title')}
            </h2>
          </div>

          <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-5">
            {t('home.benefits.paragraphs', { returnObjects: true }).map((paragraph, index) => (
              <p key={index} className={index === 1 ? "font-semibold text-[#2D3748] italic" : ""}>
                {paragraph}
              </p>
            ))}
          </div>
        </div>
      </section>

      {/* Services Preview Section */}
      <section className="py-16 md:py-24 bg-[#F7FAFC]" data-testid="services-preview-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl md:text-4xl font-bold font-['Manrope'] text-[#2D3748] mb-4">
              {t('home.servicesPreview.title')}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {t('home.servicesPreview.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mb-12">
            {services.map((service, index) => {
              const CardWrapper = service.link ? Link : 'div';
              const wrapperProps = service.link ? { to: service.link, className: "block" } : {};
              
              return (
                <CardWrapper key={index} {...wrapperProps}>
                  <Card
                    className={`group border bg-white hover:shadow-xl transition-all duration-300 hover:-translate-y-1 service-card ${service.link ? 'cursor-pointer' : ''}`}
                    data-testid={`service-preview-card-${index}`}
                  >
                    <CardContent className="p-6">
                      <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-[#2D3748] text-white mb-4 group-hover:bg-orange-500 transition-colors">
                        <service.icon className="h-6 w-6" />
                      </div>
                      <h3 className="text-xl font-semibold font-['Manrope'] text-[#2D3748] mb-2">
                        {service.title}
                      </h3>
                      <p className="text-gray-600">{service.description}</p>
                    </CardContent>
                  </Card>
                </CardWrapper>
              );
            })}
          </div>

          <div className="text-center">
            <Button
              asChild
              variant="outline"
              className="border-2 border-[#2D3748] text-[#2D3748] hover:bg-[#2D3748] hover:text-white h-12 px-6"
              data-testid="services-link-button"
            >
              <Link to={getPath('services')}>
                {t('home.servicesPreview.viewAll')}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Customer Reviews Section */}
      <ReviewSection />

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-[#2D3748]" data-testid="cta-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl text-center">
          <h2 className="text-3xl md:text-4xl font-bold font-['Manrope'] text-white mb-4">
            {t('home.cta.title')}
          </h2>
          <p className="text-lg text-gray-300 mb-8 max-w-2xl mx-auto">
            {t('home.cta.subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              asChild
              size="lg"
              className="bg-orange-500 hover:bg-orange-600 text-white text-lg h-14 px-8"
              data-testid="cta-email-button"
            >
              <a href="mailto:info@mtrenovierung.de?subject=Anfrage%20für%20Renovierungsarbeiten&body=Guten%20Tag%2C%0A%0AIch%20interessiere%20mich%20für%20Ihre%20Dienstleistungen%20und%20bitte%20um%20ein%20unverbindliches%20Angebot.%0A%0AMit%20freundlichen%20Grüßen">
                <Mail className="mr-2 h-5 w-5" />
                {t('home.cta.email')}
              </a>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-[#2D3748] text-lg h-14 px-8 bg-transparent"
              data-testid="cta-phone-button"
            >
              <a href="tel:+4915207684235">
                <Phone className="mr-2 h-5 w-5" />
                +49 152 07684235
              </a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
